import { FC } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import {
  AppAccess,
  DashboardSelectorSubmoduleItem,
  DashboardSelectorModuleItem,
  AppAccessType,
} from '../../types/dashboardSelector';
import { SubmoduleFilterRegistry } from '../../hooks/useSubmoduleFilterRegistry';
import { getDemoGameFilterId } from '../../utils/utils';
import {
  actionIconStyles,
  appAccessMenuItemSx,
  appAccessMenuPaperSx,
  appAccessPlaceholderStyles,
  cellContentSx,
  cellSx,
  checkboxSx,
  copyPasteCellSx,
  disabledActionIconStyles,
  getAppAccessCellSx,
  getAppAccessSelectSx,
  getAppAccessSelectWrapperSx,
  submoduleCellSx,
  submoduleLabelStyles,
} from './styles';

const APP_ACCESS_OPTIONS: { value: AppAccess['type']; label: string }[] = [
  {
    value: AppAccessType.DEMO,
    label: `Demo (${process.env.REACT_APP_DEMO_GAME_NAME}-${process.env.REACT_APP_DEMO_GAME_ID})`,
  },
  { value: AppAccessType.FULL_ACCESS, label: 'Full Access' },
  { value: AppAccessType.CUSTOM, label: 'Custom' },
];

const getAppAccessDisplayLabel = (type: AppAccess['type'], appAccess?: AppAccess): string => {
  const appAccessTypeLabel =
    APP_ACCESS_OPTIONS.find((option) => option.value === type)?.label ?? type;
  const totalFilters = appAccess?.filters?.length ?? 0;
  const isCustom = type === AppAccessType.CUSTOM;
  if (isCustom && totalFilters > 0) {
    return `${appAccessTypeLabel} (${totalFilters})`;
  }
  return appAccessTypeLabel;
};

const isSubmoduleEditable = (submodule: DashboardSelectorSubmoduleItem): boolean =>
  submodule.editable !== false;

export type DashboardSelectorRowProps = {
  module: DashboardSelectorModuleItem;
  selectedSubmodules: DashboardSelectorSubmoduleItem[];
  isSelectedSection?: boolean;
  filterRegistry?: SubmoduleFilterRegistry;
  isViewOnly?: boolean;
  showAppAccess?: boolean;
  /** Controls visibility of copy/paste action buttons. Defaults to showAppAccess. */
  showCopyPaste?: boolean;
  isPasteEnabled?: boolean;
  clipboardDataSubmoduleId?: string;
  pastedSubmoduleIds?: Set<string>;
  /** When true, the Full Access option in the App Access dropdown is disabled. */
  disableFullAccess?: boolean;
  onToggleSelect?: (submodules: DashboardSelectorSubmoduleItem[]) => void;
  onAppAccessChange?: (submodule: DashboardSelectorSubmoduleItem, type: AppAccess['type']) => void;
  onCopyAppAccess?: (submodule: DashboardSelectorSubmoduleItem) => void;
  onPasteAppAccess?: (submodule: DashboardSelectorSubmoduleItem) => void;
  onClearClipboard?: () => void;
};

const isSubmoduleSelected = (
  submodule: DashboardSelectorSubmoduleItem,
  selectedSubmodules: DashboardSelectorSubmoduleItem[],
) => selectedSubmodules.some((s) => s.submoduleId === submodule.submoduleId);

const DashboardSelectorRow: FC<DashboardSelectorRowProps> = ({
  module,
  selectedSubmodules = [],
  isSelectedSection = false,
  filterRegistry,
  isViewOnly = false,
  showAppAccess = true,
  showCopyPaste,
  isPasteEnabled = false,
  clipboardDataSubmoduleId,
  pastedSubmoduleIds,
  disableFullAccess = false,
  onToggleSelect,
  onAppAccessChange,
  onCopyAppAccess,
  onPasteAppAccess,
  onClearClipboard,
}) => {
  const hasOnlyOneSubmodule = module.submodules.length === 1;
  const showCopyPasteActions = showCopyPaste ?? showAppAccess;
  const isPasteDisabled = isViewOnly || !isPasteEnabled;

  const isAllSubmodulesOfModuleSelected = module.submodules.every((submodule) =>
    isSubmoduleSelected(submodule, selectedSubmodules),
  );

  const isAppAccessOptionDisabled = (
    option: AppAccess['type'],
    submodule: DashboardSelectorSubmoduleItem,
  ) => {
    if (option === AppAccessType.FULL_ACCESS) return disableFullAccess;
    const submoduleFilterMap = filterRegistry?.get(submodule.submoduleId);
    if (option === AppAccessType.DEMO) {
      const demoGameFilter = getDemoGameFilterId(submoduleFilterMap ?? {});
      return !demoGameFilter;
    }
    return false;
  };

  const handleToggleSelect = (submodules: DashboardSelectorSubmoduleItem[]) => {
    if (isAllSubmodulesOfModuleSelected) {
      //Remove all submodules of the module from the selected submodules if all submodules are already selected
      const newSubmodules = selectedSubmodules.filter(
        (s) => !submodules.some((sm) => sm.submoduleId === s.submoduleId),
      );
      onToggleSelect?.(newSubmodules);
      return;
    }

    //Add the new submodules to the selected submodules if they are not already selected
    const newSubmodules = submodules.filter(
      (submodule) => !selectedSubmodules.some((s) => s.submoduleId === submodule.submoduleId),
    );
    onToggleSelect?.([...selectedSubmodules, ...newSubmodules]);
  };

  const submoduleHasPastedAccess = (submodule: DashboardSelectorSubmoduleItem) =>
    pastedSubmoduleIds?.has(submodule.submoduleId);

  return (
    <>
      {!hasOnlyOneSubmodule && (
        <Box component="tr">
          <Box component="td" sx={submoduleCellSx} onMouseDown={onClearClipboard}>
            <Box sx={cellContentSx}>
              <Checkbox
                disableRipple
                checked={isAllSubmodulesOfModuleSelected}
                disabled={isViewOnly}
                onChange={() => handleToggleSelect(module.submodules)}
                sx={checkboxSx}
              />
              <Typography
                sx={{ ...submoduleLabelStyles, color: isViewOnly ? '#6C6C6C' : '#162936' }}
              >
                {module.name}
              </Typography>
            </Box>
          </Box>
          {showAppAccess && (
            <>
              <Box component="td" sx={{ ...cellSx, width: '30%' }} onMouseDown={onClearClipboard} />
              {showCopyPasteActions && <Box component="td" sx={{ ...cellSx, width: '25%' }} />}
            </>
          )}
        </Box>
      )}
      {module.submodules.map((submodule) => {
        const appAccessType = submodule.appAccess?.type ?? '';
        const hasAppAccess = Boolean(appAccessType);
        const isEditable = isSubmoduleEditable(submodule);
        const isCopyDisabled = isViewOnly || !hasAppAccess;
        const hasPastedAccess = Boolean(submoduleHasPastedAccess(submodule));
        const isCopiedSubmodule = submodule.submoduleId === clipboardDataSubmoduleId;
        return (
          <Box key={submodule.submoduleId} component="tr">
            <Box
              component="td"
              onMouseDown={onClearClipboard}
              sx={{
                ...submoduleCellSx,
                pl: hasOnlyOneSubmodule ? 0 : '28px',
              }}
            >
              <Box sx={cellContentSx}>
                <Checkbox
                  disableRipple
                  checked={isSelectedSection}
                  disabled={isViewOnly || !isEditable}
                  onChange={() => handleToggleSelect([submodule])}
                  sx={checkboxSx}
                />
                <Typography
                  sx={{ ...submoduleLabelStyles, color: isViewOnly ? '#6C6C6C' : '#162936' }}
                >
                  {submodule.name}
                </Typography>
              </Box>
            </Box>

            {showAppAccess && (
              <>
                <Box
                  component="td"
                  onMouseDown={onClearClipboard}
                  sx={getAppAccessCellSx(hasPastedAccess)}
                >
                  <Box sx={getAppAccessSelectWrapperSx(hasPastedAccess)}>
                    <Select
                      displayEmpty
                      fullWidth
                      value={appAccessType}
                      disabled={isViewOnly || !isEditable}
                      onChange={(event: SelectChangeEvent<AppAccess['type'] | ''>) => {
                        const { value } = event.target;
                        if (!value) return;
                        onAppAccessChange?.(submodule, value as AppAccess['type']);
                      }}
                      renderValue={(value) => {
                        if (!value) {
                          return (
                            <Typography sx={appAccessPlaceholderStyles}>Select Apps</Typography>
                          );
                        }

                        return getAppAccessDisplayLabel(
                          value as AppAccess['type'],
                          submodule.appAccess,
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: appAccessMenuPaperSx,
                        },
                        MenuListProps: {
                          sx: { py: '4px' },
                        },
                      }}
                      sx={getAppAccessSelectSx(hasPastedAccess)}
                    >
                      {APP_ACCESS_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={appAccessMenuItemSx}
                          disabled={isAppAccessOptionDisabled(option.value, submodule)}
                          onClick={
                            option.value === AppAccessType.CUSTOM &&
                            appAccessType === AppAccessType.CUSTOM
                              ? () => onAppAccessChange?.(submodule, AppAccessType.CUSTOM)
                              : undefined
                          }
                        >
                          {option.value === AppAccessType.CUSTOM ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <span>{option.label}</span>
                              <ChevronRightIcon sx={{ fontSize: '18px', color: '#1B1C17' }} />
                            </Box>
                          ) : (
                            option.label
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                {showCopyPasteActions && (
                  <Box component="td" sx={copyPasteCellSx}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box
                        sx={{ ...cellContentSx, gap: '0.25rem' }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <Tooltip title="Copy app access">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isCopyDisabled || !isEditable}
                              onClick={() => onCopyAppAccess?.(submodule)}
                              aria-label="Copy app access"
                            >
                              <ContentCopyOutlinedIcon
                                sx={
                                  isCopyDisabled
                                    ? disabledActionIconStyles
                                    : isCopiedSubmodule
                                      ? { ...actionIconStyles, color: '#162C36' }
                                      : actionIconStyles
                                }
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Paste app access">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isPasteDisabled || isCopiedSubmodule || !isEditable}
                              onClick={() => onPasteAppAccess?.(submodule)}
                              aria-label="Paste app access"
                            >
                              {submoduleHasPastedAccess(submodule) ? (
                                <InventoryOutlinedIcon sx={{ color: '#162C36' }} />
                              ) : (
                                <ContentPasteOutlinedIcon
                                  sx={
                                    isPasteDisabled || isCopiedSubmodule
                                      ? disabledActionIconStyles
                                      : actionIconStyles
                                  }
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
      })}
    </>
  );
};

export default DashboardSelectorRow;
